import { PhoneMockup } from './PhoneMockup'

const FEATURES = [
  {
    icon: '📅',
    title: 'Family Calendar',
    desc: 'Shared events with per-member color coding, recurring reminders, and real-time sync so everyone sees the same picture — from any device.',
    bullets: ['Per-member color coding', 'Recurring events & reminders', 'Drag-and-drop rescheduling', 'Offline access'],
    screen: () => (
      <div className="px-3 py-3">
        <div className="text-[10px] font-bold text-text-primary mb-2">Today, April 18</div>
        {[
          { label: 'School drop-off', color: '#f43f5e', time: '8:00' },
          { label: 'Team standup', color: '#0ea5e9', time: '9:00' },
          { label: 'Soccer practice', color: '#f43f5e', time: '16:00' },
          { label: 'Family dinner', color: '#10b981', time: '18:30' },
        ].map(({ label, color, time }) => (
          <div key={label} className="flex items-center gap-2 bg-white rounded-lg px-2 py-1.5 mb-1.5 shadow-sm">
            <div className="w-1 h-8 rounded-full flex-shrink-0" style={{ backgroundColor: color }} />
            <div>
              <div className="text-[10px] font-semibold text-text-primary">{label}</div>
              <div className="text-[9px] text-text-muted">{time}</div>
            </div>
          </div>
        ))}
      </div>
    ),
  },
  {
    icon: '✅',
    title: 'Chore Tracking',
    desc: 'Assign tasks to household members, set due dates, and celebrate completions with customizable stamp reward cards. Accountability made fun.',
    bullets: ['Assign to any member', 'Due dates & recurring tasks', 'Stamp reward cards', 'Completion history'],
    screen: () => (
      <div className="px-3 py-3">
        <div className="text-[10px] font-bold text-text-primary mb-2">This week</div>
        {[
          { label: 'Take out trash', member: 'Alex', done: true, color: '#0ea5e9' },
          { label: 'Vacuum living room', member: 'Sam', done: false, color: '#f43f5e' },
          { label: 'Dishwasher', member: 'Alex', done: true, color: '#0ea5e9' },
          { label: 'Mow the lawn', member: 'Jamie', done: false, color: '#10b981' },
        ].map(({ label, member, done, color }) => (
          <div key={label} className="flex items-center gap-2 bg-white rounded-lg px-2 py-1.5 mb-1.5 shadow-sm">
            <div className={`w-4 h-4 rounded-md border-2 flex items-center justify-center flex-shrink-0 ${done ? 'border-emerald-500 bg-emerald-500' : 'border-border'}`}>
              {done && <span className="text-white text-[8px]">✓</span>}
            </div>
            <div className="flex-1">
              <div className={`text-[10px] font-medium ${done ? 'line-through text-text-muted' : 'text-text-primary'}`}>{label}</div>
            </div>
            <div className="text-[9px] font-medium px-1.5 py-0.5 rounded-full text-white" style={{ backgroundColor: color }}>{member}</div>
          </div>
        ))}
      </div>
    ),
  },
  {
    icon: '🛒',
    title: 'Shopping Lists',
    desc: 'Real-time shared lists that update instantly across all devices. Check items off from the store while others add from home. No more duplicate buys.',
    bullets: ['Real-time sync across devices', 'Categorized by aisle', 'Auto-add from meal plan', 'Barcode scanner'],
    screen: () => (
      <div className="px-3 py-3">
        <div className="text-[10px] font-bold text-text-primary mb-2">Weekly Shop</div>
        {[
          { label: 'Whole milk (2)', cat: 'Dairy', done: true },
          { label: 'Sourdough bread', cat: 'Bakery', done: true },
          { label: 'Chicken thighs', cat: 'Meat', done: false },
          { label: 'Bell peppers', cat: 'Produce', done: false },
          { label: 'Greek yogurt', cat: 'Dairy', done: false },
        ].map(({ label, cat, done }) => (
          <div key={label} className={`flex items-center gap-2 rounded-lg px-2 py-1.5 mb-1 ${done ? 'opacity-40' : 'bg-white shadow-sm'}`}>
            <div className={`w-3.5 h-3.5 rounded border flex-shrink-0 ${done ? 'bg-emerald-500 border-emerald-500' : 'border-border'}`} />
            <span className={`text-[10px] flex-1 ${done ? 'line-through text-text-muted' : 'text-text-primary font-medium'}`}>{label}</span>
            <span className="text-[8px] text-text-muted">{cat}</span>
          </div>
        ))}
      </div>
    ),
  },
  {
    icon: '🍽️',
    title: 'Meal Planning',
    desc: 'Plan the week\'s meals, save your favourite recipes, and auto-generate a shopping list. Dinner sorted — every night.',
    bullets: ['Weekly meal plan view', 'Recipe library', 'Auto-generate shopping list', 'Import from any website'],
    screen: () => (
      <div className="px-3 py-3">
        <div className="text-[10px] font-bold text-text-primary mb-2">This week</div>
        {['Mon','Tue','Wed','Thu','Fri'].map((day, i) => {
          const meals = ['Pasta bake','Chicken tacos','Stir fry','Pizza night','Fish & chips']
          return (
            <div key={day} className="flex items-center gap-2 bg-white rounded-lg px-2 py-1.5 mb-1.5 shadow-sm">
              <span className="text-[9px] font-bold text-text-muted w-6">{day}</span>
              <span className="text-[10px] text-text-primary font-medium">{meals[i]}</span>
            </div>
          )
        })}
      </div>
    ),
  },
  {
    icon: '💰',
    title: 'Budget & Bills',
    desc: 'Track household spending by category, set budgets, and get ahead of recurring bill due dates. Know exactly where every dollar goes.',
    bullets: ['Spending by category', 'Bill reminders', 'Monthly budget goals', 'Export reports'],
    screen: () => (
      <div className="px-3 py-3">
        <div className="text-[10px] font-bold text-text-primary mb-1">April budget</div>
        <div className="text-xl font-bold text-text-primary mb-2">$1,240 <span className="text-xs text-text-muted font-normal">/ $2,000</span></div>
        {[
          { label: 'Groceries', pct: 65, color: '#10b981' },
          { label: 'Utilities', pct: 40, color: '#0ea5e9' },
          { label: 'Entertainment', pct: 80, color: '#f43f5e' },
        ].map(({ label, pct, color }) => (
          <div key={label} className="mb-2">
            <div className="flex justify-between text-[9px] mb-0.5">
              <span className="text-text-secondary">{label}</span>
              <span className="text-text-muted">{pct}%</span>
            </div>
            <div className="h-1.5 bg-border rounded-full overflow-hidden">
              <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: color }} />
            </div>
          </div>
        ))}
      </div>
    ),
  },
  {
    icon: '🔐',
    title: 'Secure Vault',
    desc: 'AES-256 encrypted storage for Wi-Fi passwords, PINs, family credentials, and sensitive notes. Only your household can see it.',
    bullets: ['AES-256 encryption', 'Wi-Fi, PINs, accounts', 'Secure notes', 'Household-only access'],
    screen: () => (
      <div className="px-3 py-3">
        <div className="text-[10px] font-bold text-text-primary mb-2">Vault</div>
        {[
          { label: 'Home Wi-Fi', type: 'Wi-Fi', icon: '📶' },
          { label: 'Netflix', type: 'Account', icon: '📺' },
          { label: 'Garage code', type: 'PIN', icon: '🔢' },
          { label: 'Insurance card', type: 'Document', icon: '📄' },
        ].map(({ label, type, icon }) => (
          <div key={label} className="flex items-center gap-2 bg-white rounded-lg px-2 py-1.5 mb-1.5 shadow-sm">
            <span className="text-base">{icon}</span>
            <div className="flex-1">
              <div className="text-[10px] font-semibold text-text-primary">{label}</div>
              <div className="text-[9px] text-text-muted">{type}</div>
            </div>
            <div className="text-[9px] text-text-muted">••••</div>
          </div>
        ))}
      </div>
    ),
  },
]

const FeatureRow = ({ feature, index }) => {
  const isEven = index % 2 === 0
  const Screen = feature.screen

  return (
    <div className={`flex flex-col ${isEven ? 'lg:flex-row' : 'lg:flex-row-reverse'} items-center gap-12 py-20`}>
      {/* Copy */}
      <div className={`flex-1 text-center lg:text-left ${isEven ? 'reveal-left' : 'reveal-right'}`}>
        <div className="text-4xl mb-4">{feature.icon}</div>
        <h3 className="font-display text-2xl sm:text-3xl font-bold text-text-primary mb-4">
          {feature.title}
        </h3>
        <p className="text-text-secondary leading-relaxed mb-6 max-w-md mx-auto lg:mx-0">
          {feature.desc}
        </p>
        <ul className="space-y-2 inline-block text-left">
          {feature.bullets.map((b) => (
            <li key={b} className="flex items-center gap-2 text-sm text-text-secondary">
              <span className="w-4 h-4 bg-emerald-100 rounded-full flex items-center justify-center text-emerald-600 flex-shrink-0 text-[9px]">✓</span>
              {b}
            </li>
          ))}
        </ul>
      </div>

      {/* Phone */}
      <div className={`flex-1 flex justify-center ${isEven ? 'reveal-right' : 'reveal-left'}`}>
        <PhoneMockup tilt={isEven ? 'right' : 'left'}>
          <Screen />
        </PhoneMockup>
      </div>
    </div>
  )
}

export const FeaturesSection = () => (
  <section id="features" className="py-12 px-4">
    <div className="max-w-5xl mx-auto">
      <div className="text-center mb-4 reveal">
        <p className="text-primary text-sm font-semibold uppercase tracking-wider mb-3">Everything you need</p>
        <h2 className="font-display text-3xl sm:text-4xl font-bold text-text-primary mb-4">
          Six tools. One app. Zero chaos.
        </h2>
        <p className="text-text-secondary max-w-xl mx-auto">
          Each feature is designed specifically for households — built around how families actually work.
        </p>
      </div>

      <div className="divide-y divide-border">
        {FEATURES.map((feature, i) => (
          <FeatureRow key={feature.title} feature={feature} index={i} />
        ))}
      </div>
    </div>
  </section>
)
